import React, { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import Icon from '../../Icon';
import Truncate from '../../Truncate';

import styles from './Checkbox2.module.scss';

const Checkbox2 = ({
  checked,
  className,
  disabled,
  id,
  label,
  name,
  onChange,
  required,
  halfchecked,
  fullWidth,
  labelClassName,
  theme,
  ...rest
}) => {
  const cnInputCheck = cn(styles.inputCheck, styles[theme], className, {
    [styles.fullWidth]: fullWidth,
    [styles.required]: required,
    [styles.disabled]: disabled
  });

  const cnLabel = cn(styles.label, styles[theme], labelClassName, {
    [styles.disabled]: disabled
  });

  const checkbox = useMemo(
    () => (
      <label className={styles.customInput} htmlFor={id}>
        {(checked || halfchecked) && (
          <Icon
            label={!halfchecked ? 'check' : 'minus'}
            theme="solid"
            className={styles.icon}
            width={10}
            fill="#fff"
          />
        )}
      </label>
    ),
    [id, checked, halfchecked]
  );

  const handleChange = useCallback(
    () => onChange({ id: parseInt(id, 10), label }),
    [onChange, id, label],
  );

  return (
    <div className={cnInputCheck}>
      <input
        {...rest}
        type="checkbox"
        id={id}
        data-id={id}
        aria-describedby={label}
        checked={checked}
        disabled={disabled}
        name={name}
        onChange={handleChange}
        required={required}
      />
      {checkbox}
      {label && (
        <label htmlFor={id} className={cnLabel}>
          <Truncate>
            {label}
          </Truncate>
        </label>
      )}
    </div>
  );
};

Checkbox2.displayName = 'Checkbox2';

Checkbox2.propTypes = {
  id: PropTypes.string.isRequired,
  checked: PropTypes.bool,
  halfchecked: PropTypes.bool,
  className: PropTypes.string,
  labelClassName: PropTypes.string,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  theme: PropTypes.string,
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.func,
    PropTypes.node,
    PropTypes.bool
  ]),
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  required: PropTypes.bool
};

Checkbox2.defaultProps = {
  checked: false,
  halfchecked: false,
  className: undefined,
  labelClassName: undefined,
  disabled: false,
  fullWidth: false,
  theme: 'KolLab',
  label: null,
  required: false,
  onChange: () => {}
};

export default memo(Checkbox2);
